/**
 * Export confirmation dialog for creative evaluation data.
 *
 * Shows a summary of what will be exported (folder count, file count, brand),
 * then POSTs to the backend to build a zip and triggers the download.
 *
 * Supports two modes: "all" (every loaded image) or "selected" (only images
 * the user has checked via the card-level export checkbox).
 *
 * Uses the same sandboxed modal pattern as DevToolsModal — intentionally
 * independent of Flux/Tailwind to stay consistent with the creative-eval page.
 */

import { useMemo, useState } from "react";
import post from "@adora/shared/utils/post";
import { RelativeUrl } from "@adora/shared/utils/environment";
import { createProgressToast } from "@adora/shared/utils/progress-toast";
import { fetchBlobFromPresignedUrl } from "@adora/shared/utils/download";
import type { EvalImage, ExportEvalsResponse } from "../../types/creative-eval";
import { computeExportSummary } from "./helpers";

type ExportMode = "all" | "selected";

interface ExportDialogProps {
  images: EvalImage[];
  selectedIds: Set<number>;
  onClose: () => void;
}

function formatZipFilename(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `creative_evaluation_export_${date}_${time}.zip`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const ExportDialog: React.FC<ExportDialogProps> = ({ images, selectedIds, onClose }) => {
  const hasSelection = selectedIds.size > 0;
  const [exportMode, setExportMode] = useState<ExportMode>(hasSelection ? "selected" : "all");
  const zipFilename = useMemo(() => formatZipFilename(), []);

  const activeImages = useMemo(
    () => (exportMode === "selected" ? images.filter((img) => selectedIds.has(img.creative_id)) : images),
    [exportMode, images, selectedIds]
  );

  const summary = useMemo(() => computeExportSummary(activeImages), [activeImages]);
  const creativeIds = useMemo(() => activeImages.map((img) => img.creative_id), [activeImages]);

  const handleExport = async () => {
    onClose();

    const progress = createProgressToast("Building export…", { indeterminate: true });

    const result = await post<ExportEvalsResponse>(
      `${RelativeUrl}/creative-eval/export`,
      {},
      { creative_ids: creativeIds }
    );

    result.match(
      (error) => {
        progress.error(`Export failed: ${error.message}`);
      },
      async (data) => {
        try {
          const blob = await fetchBlobFromPresignedUrl(data.download_url);
          progress.success("Export ready — downloading…");
          downloadBlob(blob, zipFilename);
        } catch {
          progress.error("Export failed: could not download file");
        }
      }
    );
  };

  return (
    <div className="query-overlay open">
      <button aria-label="Close export dialog" className="query-backdrop" onClick={onClose} type="button" />
      <div className="query-modal export-modal">
        <div className="query-modal-header">
          <h3>Export Creative Evaluations</h3>
          <div className="query-modal-actions">
            <button className="query-close-btn" onClick={onClose} type="button">
              Close
            </button>
          </div>
        </div>

        <div className="export-body">
          <div className="export-mode-toggle">
            <button
              className={`export-mode-btn ${exportMode === "all" ? "active" : ""}`}
              onClick={() => setExportMode("all")}
              type="button"
            >
              Export All ({images.length})
            </button>
            <button
              className={`export-mode-btn ${exportMode === "selected" ? "active" : ""}`}
              onClick={() => hasSelection && setExportMode("selected")}
              disabled={!hasSelection}
              type="button"
            >
              Export Selected{hasSelection ? ` (${selectedIds.size})` : ""}
            </button>
          </div>

          <div className="export-summary">
            <div className="export-row">
              <span className="export-label">Brand</span>
              <span className="export-value">{summary.brandName}</span>
            </div>
            <div className="export-row">
              <span className="export-label">Image folders</span>
              <span className="export-value">{summary.folderCount}</span>
            </div>
            <div className="export-row">
              <span className="export-label">Estimated files</span>
              <span className="export-value">{summary.totalFiles}</span>
            </div>
            <div className="export-row">
              <span className="export-label">Zip filename</span>
              <span className="export-value export-filename">{zipFilename}</span>
            </div>
          </div>

          <p className="export-note">Files will be downloaded to your browser&apos;s default Downloads folder.</p>
        </div>

        <div className="export-actions">
          <button className="query-close-btn" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="primary-btn accent-primary"
            onClick={handleExport}
            disabled={activeImages.length === 0}
            type="button"
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
